import axios from 'axios'
import { useContext, useEffect, useState } from 'react'
import { Container, Skeleton, Tab, Tabs, Divider } from '@mui/material'
import './CustomerList.css'
import { CreditCardOutlined, ReceiptOutlined, PaymentsOutlined } from '@mui/icons-material'
import { useParams, useNavigate } from 'react-router-dom'
import { dataContext } from '..'
import InvoiceList, { InvoiceSearch } from './Invoice/InvoiceList'
import PaymentList, { PaymentSearch } from './Payment/PaymentList'
import CreditList, { CreditSearch } from './CustomerCredit/CreditList'
import { customerCreditType, invoiceType, receivePaymentType } from '../types'

function CustomerList() {
  const { tab: urlTab } = useParams()
  const navigate = useNavigate()
  const { user } = useContext(dataContext)

  const [tab, setTab] = useState<string>(urlTab ?? 'invoices')
  const [invoiceList, setInvoiceList] = useState<
    (invoiceType & {
      customer_name: string
      job_name: string
    })[]
  >([])
  const [invoiceCount, setInvoiceCount] = useState(0)
  const [invoiceFilters, setInvoiceFilters] = useState<{ [index: string]: string | number }>({
    page: 0,
    limit: 25,
    sort: 'due_date desc',
    invoiceNumber: '',
    invoiceStatus: '',
    customerName: '',
    fromDate: '',
    toDate: '',
    dueDateStart: '',
    dueDateEnd: '',
    soNumber: '',
    serialNumber: '',
  })
  const [paymentList, setPaymentList] = useState<
    (receivePaymentType & {
      customer_name: string
      job_name: string
    })[]
  >([])
  const [paymentCount, setPaymentCount] = useState(0)
  const [paymentFilters, setPaymentFilters] = useState<{ [index: string]: string | number }>({
    page: 0,
    limit: 25,
    sort: 'txn_date desc',
    paymentNumber: '',
    customerPoNumber: '',
    customerJobName: '',
    fromDate: '',
    toDate: '',
  })
  const [creditList, setCreditList] = useState<
    (customerCreditType & {
      customer_name: string
      job_name: string
    })[]
  >([])
  const [creditCount, setCreditCount] = useState(0)
  const [creditFilters, setCreditFilters] = useState<{ [index: string]: string | number }>({
    page: 0,
    limit: 25,
    sort: 'txn_date desc',
    customerJobName: '',
    creditNumber: '',
    fromDate: '',
    toDate: '',
    invoiceNumber: '',
  })
  const [listLoading, setListLoading] = useState(true)
  const [initialLoad, setInitialLoad] = useState(true)
  const [searchError, setSearchError] = useState(false)

  useEffect(() => {
    setTab(urlTab ?? 'invoices')
    document.title = `${
      urlTab === 'invoices' ? 'Invoices' : urlTab === 'payments' ? 'Payments' : 'Customer Credits'
    } | Salestrack`
  }, [urlTab])

  const handleInvoiceSearch = (filters: { [index: string]: string | number }) => {
    let searchEndpoint = `/listInvoices?`
    for (const key of Object.keys(filters)) {
      if (filters[key]) {
        searchEndpoint += `${key}=${filters[key]}&`
      }
    }
    if (user.user_type === 'Sales Rep') {
      searchEndpoint += `salesUser=${user.user_id}&`
    }

    let loading = true
    setTimeout(() => {
      setListLoading(loading)
    }, 200)

    axios
      .get(searchEndpoint)
      .then(res => {
        setInvoiceList(res.data.data)
        setInvoiceCount(res.data.count)
        setSearchError(false)
      })
      .catch(err => {
        console.error(err)
        setSearchError(true)
      })
      .finally(() => {
        loading = false
        setListLoading(false)
        setInitialLoad(false)
      })
  }

  const handlePaymentSearch = (filters: { [index: string]: string | number }) => {
    let searchEndpoint = `/listPayments?`
    for (const key of Object.keys(filters)) {
      if (filters[key]) {
        searchEndpoint += `${key}=${filters[key]}&`
      }
    }

    let loading = true
    setTimeout(() => {
      setListLoading(loading)
    }, 200)

    axios
      .get(searchEndpoint)
      .then(res => {
        setPaymentList(res.data.data)
        setPaymentCount(res.data.count)
        setSearchError(false)
      })
      .catch(err => {
        console.error(err)
        setSearchError(true)
      })
      .finally(() => {
        loading = false
        setListLoading(false)
        setInitialLoad(false)
      })
  }

  const handleCreditSearch = (filters: { [index: string]: string | number }) => {
    let searchEndpoint = `/listCredits?`
    for (const key of Object.keys(filters)) {
      if (filters[key]) {
        searchEndpoint += `${key}=${filters[key]}&`
      }
    }

    let loading = true
    setTimeout(() => {
      setListLoading(loading)
    }, 200)

    axios
      .get(searchEndpoint)
      .then(res => {
        setCreditList(res.data.data)
        setCreditCount(res.data.count)
        setSearchError(false)
      })
      .catch(err => {
        console.error(err)
        setSearchError(true)
      })
      .finally(() => {
        loading = false
        setListLoading(false)
        setInitialLoad(false)
      })
  }

  return (
    <div className="customer-list">
      <Container className="customer-filters" maxWidth={false}>
        <div className="customer-tabs">
          {initialLoad && listLoading ? (
            <div className="customer-tabs-loading">
              <Skeleton width={100} height={48} />
              <Skeleton width={100} height={48} />
              <Skeleton width={140} height={48} />
            </div>
          ) : (
            <Tabs
              value={tab}
              onChange={(_e, newValue) => {
                setTab(newValue)
                navigate(`/customer/${newValue}`)
              }}
            >
              <Tab value="invoices" label="Invoices" icon={<ReceiptOutlined />} />
              <Tab value="payments" label="Payments" icon={<PaymentsOutlined />} />
              <Tab value="credits" label="Customer Credits" icon={<CreditCardOutlined />} />
            </Tabs>
          )}
        </div>
        <Divider />
        {tab === 'invoices' && (
          <InvoiceSearch
            filters={invoiceFilters}
            setFilters={setInvoiceFilters}
            handleSearch={handleInvoiceSearch}
            initialLoad={initialLoad}
            listLoading={listLoading}
          />
        )}
        {tab === 'payments' && (
          <PaymentSearch
            filters={paymentFilters}
            setFilters={setPaymentFilters}
            handleSearch={handlePaymentSearch}
            initialLoad={initialLoad}
            listLoading={listLoading}
          />
        )}
        {tab === 'credits' && (
          <CreditSearch
            filters={creditFilters}
            setFilters={setCreditFilters}
            handleSearch={handleCreditSearch}
            initialLoad={initialLoad}
            listLoading={listLoading}
          />
        )}
      </Container>
      {tab === 'invoices' && (
        <div className="invoices-container">
          <InvoiceList
            filters={invoiceFilters}
            setFilters={setInvoiceFilters}
            handleSearch={handleInvoiceSearch}
            invoiceList={invoiceList}
            invoiceCount={invoiceCount}
            searchError={searchError}
            initialLoad={initialLoad}
            listLoading={listLoading}
          />
        </div>
      )}
      {tab === 'payments' && (
        <div className="payments-container">
          <PaymentList
            paymentList={paymentList}
            paymentCount={paymentCount}
            filters={paymentFilters}
            searchError={searchError}
            initialLoad={initialLoad}
            listLoading={listLoading}
            handleSearch={handlePaymentSearch}
            setFilters={setPaymentFilters}
          />
        </div>
      )}
      {tab === 'credits' && (
        <div className="credits-container">
          <CreditList
            creditList={creditList}
            creditCount={creditCount}
            filters={creditFilters}
            searchError={searchError}
            initialLoad={initialLoad}
            listLoading={listLoading}
            handleSearch={handleCreditSearch}
            setFilters={setCreditFilters}
          />
        </div>
      )}
    </div>
  )
}

export default CustomerList
